import { differenceInDays } from "date-fns";

export interface DayGroup<T> {
  dayNumber: number;
  date: Date | null;
  items: T[];
}

interface ItemWithDates {
  item_type: string;
  date?: string | null;
  check_in?: string | null;
}

/**
 * Parse a date string as UTC to avoid timezone offset issues.
 * When JS parses "2026-03-10", it creates midnight UTC which displays
 * as the previous day in positive UTC offset timezones (e.g., SAST).
 */
function parseAsUTC(dateStr: string): Date {
  // Handle both "2026-03-10" and "2026-03-10T00:00:00" formats
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

/**
 * Groups itinerary items by day number, calculated from the lead's start date.
 * Items are sorted by day number for consistent rendering.
 */
export function groupItemsByDay<T extends ItemWithDates>(
  items: T[],
  startDate: string | null | undefined
): DayGroup<T>[] {
  // First pass: bucket items by their date key (yyyy-MM-dd) so that
  // multiple items on the same calendar day always share one banner,
  // even when the lead has no start_date.
  const buckets = new Map<string, { date: Date | null; items: T[] }>();
  const NO_DATE_KEY = "__no_date__";

  items.forEach((item) => {
    const relevantDate = item.item_type === 'accommodation'
      ? item.check_in
      : item.date;

    const key = relevantDate ? relevantDate.split('T')[0] : NO_DATE_KEY;

    if (!buckets.has(key)) {
      buckets.set(key, {
        date: relevantDate ? parseAsUTC(relevantDate) : null,
        items: [],
      });
    }
    buckets.get(key)!.items.push(item);
  });

  // Second pass: assign a day number to each bucket.
  // - If startDate exists and the bucket has a date → day = diff + 1
  // - Otherwise → sequential numbering based on date order, dateless last.
  const dated = Array.from(buckets.entries())
    .filter(([key]) => key !== NO_DATE_KEY)
    .sort(([a], [b]) => a.localeCompare(b));
  const undated = buckets.get(NO_DATE_KEY);

  const result: DayGroup<T>[] = dated.map(([key, { date, items: bucketItems }], idx) => {
    const dayNumber = startDate
      ? differenceInDays(parseAsUTC(key), parseAsUTC(startDate)) + 1
      : idx + 1;
    return { dayNumber, date, items: bucketItems };
  });

  if (undated) {
    const nextDayNumber = result.length
      ? Math.max(...result.map((g) => g.dayNumber)) + 1
      : 1;
    result.push({ dayNumber: nextDayNumber, date: null, items: undated.items });
  }

  return result.sort((a, b) => a.dayNumber - b.dayNumber);
}
